import React from "react";
import Badge from "../../../components/common/Badge";
import CashingUpReportPage from "./CashingUpReportPage";
import { useGetCashingUpDayClosesReportQuery } from "../../../store/api";
import {
  formatAmountWithImpact,
  formatSignedCurrency,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";
import { formatDateTime } from "../../../utils/reporting";

const columns = [
  { key: "dayCloseId", label: "DAY CLOSE" },
  { key: "businessDate", label: "BUSINESS DATE" },
  { key: "status", label: "STATUS" },
  { key: "totalTills", label: "TOTAL TILLS" },
  { key: "closedTills", label: "CLOSED TILLS" },
  { key: "totalExpectedCash", label: "EXPECTED CASH" },
  { key: "totalActualCash", label: "ACTUAL CASH" },
  { key: "totalCashVariance", label: "VARIANCE" },
  { key: "syncStatus", label: "SYNC" },
  { key: "closedAt", label: "CLOSED AT" },
];

const DayClosesReport = () => {
  return (
    <CashingUpReportPage
      title="Day Closes Report"
      subtitle="Central history of outlet day-close totals and closure status."
      useQueryHook={useGetCashingUpDayClosesReportQuery}
      collectionKeys={["dayCloses", "items", "rows", "data"]}
      columns={columns}
      showBusinessDate
      showLimit
      showExport={false}
      buildRows={(collection) =>
        collection.map((dayClose) => ({
          id: dayClose.dayCloseId || `${dayClose.storeId}-${dayClose.businessDate}`,
          dayCloseId: dayClose.dayCloseId || "-",
          businessDate: dayClose.businessDate || "-",
          statusValue: dayClose.status || "",
          status: (
            <Badge tone={getStatusTone(dayClose.status)}>
              {formatStatusLabel(dayClose.status)}
            </Badge>
          ),
          totalTills: dayClose.totalTills ?? 0,
          closedTills: dayClose.closedTills ?? 0,
          totalExpectedCashValue: Number(dayClose.totalExpectedCash || 0),
          totalExpectedCash: formatSignedCurrency(dayClose.totalExpectedCash),
          totalActualCashValue: Number(dayClose.totalActualCash || 0),
          totalActualCash: formatSignedCurrency(dayClose.totalActualCash),
          totalCashVarianceValue: Number(dayClose.totalCashVariance || 0),
          totalCashVariance: (() => {
            const delta = Number(dayClose.totalCashVariance || 0);
            const formatting = formatAmountWithImpact(delta);
            return (
              <span
                className={
                  formatting.tone === "rose"
                    ? "font-semibold text-rose-700"
                    : formatting.tone === "emerald"
                      ? "font-semibold text-emerald-700"
                      : "font-semibold text-slate-700"
                }
              >
                {formatting.value}
              </span>
            );
          })(),
          syncStatus: (
            <Badge tone={getStatusTone(dayClose.syncStatus)}>
              {formatStatusLabel(dayClose.syncStatus)}
            </Badge>
          ),
          closedAt: formatDateTime(dayClose.closedAt),
        }))
      }
      buildStats={(_, rows) => {
        const totalRows = rows.length;
        const closedRows = rows.filter((row) =>
          String(row.statusValue || "").toUpperCase().includes("CLOSED"),
        ).length;
        const reopenedRows = rows.filter((row) =>
          String(row.statusValue || "").toUpperCase().includes("REOPENED"),
        ).length;
        const varianceTotal = rows.reduce((sum, row) => {
          return sum + Number(row.totalCashVarianceValue || 0);
        }, 0);

        return [
          {
            label: "Day Closes",
            value: String(totalRows),
            helper: "Records returned by the selected store and date.",
            accent: "indigo",
          },
          {
            label: "Closed",
            value: String(closedRows),
            helper: "Day closes already finalized.",
            accent: "emerald",
          },
          {
            label: "Reopened",
            value: String(reopenedRows),
            helper: "Day closes that were reopened later.",
            accent: "amber",
          },
          {
            label: "Variance",
            value: formatSignedCurrency(varianceTotal),
            helper: "Combined variance across visible rows.",
            accent: "rose",
          },
        ];
      }}
      emptyText="No day-close records found for the selected store and business date."
    />
  );
};

export default DayClosesReport;
